import { CalculatorOutlined } from '@ant-design/icons';
import { getOrderShortCode } from '@mahawi/eshop-common';
import {
  EWarehouseOrderPairingState,
  EWarehouseOrderStatus,
  type IWarehouseDetail,
  type IWarehouseOrderAmountFilter,
  type IWarehouseOrderListFilters,
  type IWarehouseOrderListItem,
  type IWarehouseOrderPairedOrderRef,
} from '@mahawi/eshop-common/src/types';
import { Button, Popconfirm, Space, Tooltip, Typography } from 'antd';
import { type ColumnsType } from 'antd/es/table';
import {
  type FilterValue,
  type SorterResult,
  type TableCurrentDataSource,
} from 'antd/es/table/interface';
import dayjs from 'dayjs';
import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useSearchParams } from 'react-router';

import DetailPageHeader from '../components/detail-page-header';
import Link from '../components/link';
import RelativeTime from '../components/relative-time';
import TableComponent from '../components/table';
import {
  formatAmount,
  formatEur,
  WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL,
  WAREHOUSE_ORDER_PAIRING_STATE_LABEL,
  WAREHOUSE_ORDER_STATUS_LABEL,
} from '../components/warehouse-order/labels';
import ProfitText from '../components/warehouse-order/profit-text';
import {
  WarehouseOrderPairingStateTag,
  WarehouseOrderStatusTag,
} from '../components/warehouse-order/status-tag';
import SyncButton from '../components/warehouse-order/sync-button';
import { EMPTY_VALUE } from '../fragments/empty-value';
import { parseList } from '../fragments/parse-list';
import { getColumnDateRange } from '../fragments/table-get-column-date-range';
import {
  getColumnNumberRange,
  parseNumberRangeFilterKeys,
  toNumberRangeFilterKeys,
} from '../fragments/table-get-column-number-range';
import { getColumnSearchDropdown } from '../fragments/table-get-column-search';
import { useAppDispatch } from '../hooks/use-app-dispatch';
import { type RootState } from '../reducers';
import {
  warehouseOrdersLoad,
  warehouseOrdersRecalculate,
} from '../reducers/warehouse-order/reducer';
import { SPACING } from '../theme/tokens';

const { Text } = Typography;

const DEFAULT_PAGE_SIZE = 50;

export enum EWarehouseOrdersSearchParam {
  WAREHOUSE = 'warehouse',
  STATUS = 'status',
  PAIRING = 'pairing',
  FROM = 'from',
  TO = 'to',
  SEARCH = 'search',
  ORDER_SEARCH = 'order',
  ITEMS_MIN = 'itemsMin',
  ITEMS_MAX = 'itemsMax',
  COST_MIN = 'costMin',
  COST_MAX = 'costMax',
  COST_MISSING = 'costMissing',
  PROFIT_MIN = 'profitMin',
  PROFIT_MAX = 'profitMax',
  PROFIT_MISSING = 'profitMissing',
  PAGE = 'page',
  PAGE_SIZE = 'pageSize',
}

enum EWarehouseOrdersFilterColumn {
  ORDERED = 'ordered',
  WAREHOUSE = 'warehouse',
  CODE = 'code',
  STATUS = 'status',
  PAIRING = 'pairing',
  ORDERS = 'orders',
  ITEMS = 'items',
  COST = 'cost',
  PROFIT = 'profit',
}

const SEARCH_PARAM_FLAG_ON = '1';

interface IRangeSearchParams {
  min: EWarehouseOrdersSearchParam;
  max: EWarehouseOrdersSearchParam;
  missing?: EWarehouseOrdersSearchParam;
}

const ITEMS_SEARCH_PARAMS: IRangeSearchParams = {
  min: EWarehouseOrdersSearchParam.ITEMS_MIN,
  max: EWarehouseOrdersSearchParam.ITEMS_MAX,
};

const COST_SEARCH_PARAMS: IRangeSearchParams = {
  min: EWarehouseOrdersSearchParam.COST_MIN,
  max: EWarehouseOrdersSearchParam.COST_MAX,
  missing: EWarehouseOrdersSearchParam.COST_MISSING,
};

const PROFIT_SEARCH_PARAMS: IRangeSearchParams = {
  min: EWarehouseOrdersSearchParam.PROFIT_MIN,
  max: EWarehouseOrdersSearchParam.PROFIT_MAX,
  missing: EWarehouseOrdersSearchParam.PROFIT_MISSING,
};

const STATUS_FILTERS = Object.values(EWarehouseOrderStatus).map(
  (status: EWarehouseOrderStatus) => ({
    value: status,
    text: WAREHOUSE_ORDER_STATUS_LABEL[status],
  }),
);

const PAIRING_FILTERS = Object.values(EWarehouseOrderPairingState).map(
  (pairingState: EWarehouseOrderPairingState) => ({
    value: pairingState,
    text: WAREHOUSE_ORDER_PAIRING_STATE_LABEL[pairingState],
  }),
);

type TWarehouseOrderRow = IWarehouseOrderListItem & { key: string };

const joinFilterValue = (value: FilterValue | null | undefined): string =>
  (value ?? []).map(String).join(',');

const parseNumber = (value: string | null): number | undefined => {
  if (value === null || value.trim() === '') {
    return undefined;
  }

  const parsed = Number(value);

  return Number.isFinite(parsed) ? parsed : undefined;
};

const parseRange = (
  searchParams: URLSearchParams,
  params: IRangeSearchParams,
): IWarehouseOrderAmountFilter | undefined => {
  if (
    params.missing &&
    searchParams.get(params.missing) === SEARCH_PARAM_FLAG_ON
  ) {
    return { isMissing: true };
  }

  const min: number | undefined = parseNumber(searchParams.get(params.min));
  const max: number | undefined = parseNumber(searchParams.get(params.max));

  return min === undefined && max === undefined ? undefined : { min, max };
};

const toRangeSearchParams = (
  value: FilterValue | null | undefined,
  params: IRangeSearchParams,
): Record<string, string> => {
  const filter: IWarehouseOrderAmountFilter = parseNumberRangeFilterKeys(value);

  return {
    [params.min]: filter.min === undefined ? '' : String(filter.min),
    [params.max]: filter.max === undefined ? '' : String(filter.max),
    ...(params.missing
      ? { [params.missing]: filter.isMissing ? SEARCH_PARAM_FLAG_ON : '' }
      : {}),
  };
};

const parseFilters = (
  searchParams: URLSearchParams,
): IWarehouseOrderListFilters => {
  const statuses: string[] = parseList(
    searchParams.get(EWarehouseOrdersSearchParam.STATUS),
  );
  const pairingStates: string[] = parseList(
    searchParams.get(EWarehouseOrdersSearchParam.PAIRING),
  );
  const from: string | null = searchParams.get(
    EWarehouseOrdersSearchParam.FROM,
  );
  const to: string | null = searchParams.get(EWarehouseOrdersSearchParam.TO);
  const search: string = (
    searchParams.get(EWarehouseOrdersSearchParam.SEARCH) ?? ''
  ).trim();
  const orderSearch: string = (
    searchParams.get(EWarehouseOrdersSearchParam.ORDER_SEARCH) ?? ''
  ).trim();

  return {
    warehouseUuids: parseList(
      searchParams.get(EWarehouseOrdersSearchParam.WAREHOUSE),
    ),
    statuses: Object.values(EWarehouseOrderStatus).filter(
      (status: EWarehouseOrderStatus) => statuses.includes(status),
    ),
    pairingStates: Object.values(EWarehouseOrderPairingState).filter(
      (pairingState: EWarehouseOrderPairingState) =>
        pairingStates.includes(pairingState),
    ),
    from: from ? dayjs(from).startOf('day').toISOString() : undefined,
    to: to ? dayjs(to).add(1, 'day').startOf('day').toISOString() : undefined,
    search: search || undefined,
    orderSearch: orderSearch || undefined,
    itemCount: parseRange(searchParams, ITEMS_SEARCH_PARAMS),
    purchasePriceEur: parseRange(searchParams, COST_SEARCH_PARAMS),
    profitEur: parseRange(searchParams, PROFIT_SEARCH_PARAMS),
  };
};

const getColumns = ({
  filters,
  searchParams,
  warehouses,
}: {
  filters: IWarehouseOrderListFilters;
  searchParams: URLSearchParams;
  warehouses: IWarehouseDetail[];
}): ColumnsType<TWarehouseOrderRow> => {
  const from: string | null = searchParams.get(
    EWarehouseOrdersSearchParam.FROM,
  );
  const to: string | null = searchParams.get(EWarehouseOrdersSearchParam.TO);

  return [
    {
      title: 'Ordered',
      key: EWarehouseOrdersFilterColumn.ORDERED,
      dataIndex: 'orderedAt',
      ...getColumnDateRange<TWarehouseOrderRow>(),
      filteredValue: from && to ? [from, to] : [],
      render: (orderedAt: string): React.ReactElement => (
        <RelativeTime value={orderedAt} />
      ),
    },
    {
      title: 'Warehouse',
      key: EWarehouseOrdersFilterColumn.WAREHOUSE,
      filters: warehouses
        .filter((warehouse: IWarehouseDetail): boolean => !warehouse.isInternal)
        .map((warehouse: IWarehouseDetail) => ({
          value: warehouse.uuid,
          text: warehouse.name,
        })),
      filteredValue: filters.warehouseUuids,
      render: (_value: string, record: TWarehouseOrderRow): string =>
        record.warehouse.name,
    },
    {
      title: 'Code',
      key: EWarehouseOrdersFilterColumn.CODE,
      dataIndex: 'code',
      ...getColumnSearchDropdown<TWarehouseOrderRow>(
        'Code, note or product code',
      ),
      filteredValue: filters.search ? [filters.search] : [],
      render: (
        code: string,
        record: TWarehouseOrderRow,
      ): React.ReactElement => (
        <Link
          to={`/orders/warehouse-order/${record.uuid}`}
          label={code}
          openInNewTab
        />
      ),
    },
    {
      title: 'Status',
      key: EWarehouseOrdersFilterColumn.STATUS,
      dataIndex: 'status',
      filters: STATUS_FILTERS,
      filteredValue: filters.statuses,
      render: (status: EWarehouseOrderStatus): React.ReactElement => (
        <WarehouseOrderStatusTag status={status} />
      ),
    },
    {
      title: 'Pairing',
      key: EWarehouseOrdersFilterColumn.PAIRING,
      dataIndex: 'pairingState',
      filters: PAIRING_FILTERS,
      filteredValue: filters.pairingStates,
      render: (
        pairingState: EWarehouseOrderPairingState,
      ): React.ReactElement => (
        <WarehouseOrderPairingStateTag pairingState={pairingState} />
      ),
    },
    {
      title: 'Our orders',
      key: EWarehouseOrdersFilterColumn.ORDERS,
      ...getColumnSearchDropdown<TWarehouseOrderRow>('Our order code'),
      filteredValue: filters.orderSearch ? [filters.orderSearch] : [],
      render: (_value: string, record: TWarehouseOrderRow): React.ReactNode =>
        record.orders.length === 0 ? (
          EMPTY_VALUE
        ) : (
          <Space size={SPACING.xs} wrap>
            {record.orders.map(
              (order: IWarehouseOrderPairedOrderRef): React.ReactElement => (
                <Tooltip
                  key={order.uuid}
                  title={WAREHOUSE_ORDER_PAIRING_SOURCE_LABEL[order.source]}
                >
                  <span>
                    <Link
                      to={`/order/${order.uuid}`}
                      label={getOrderShortCode(order.uuid)}
                      openInNewTab
                    />
                  </span>
                </Tooltip>
              ),
            )}
          </Space>
        ),
    },
    {
      title: 'Items',
      key: EWarehouseOrdersFilterColumn.ITEMS,
      dataIndex: 'itemCount',
      align: 'right',
      ...getColumnNumberRange<TWarehouseOrderRow>(),
      filteredValue: toNumberRangeFilterKeys(filters.itemCount),
    },
    {
      title: 'Total without VAT',
      align: 'right',
      render: (_value: string, record: TWarehouseOrderRow): string =>
        formatAmount(record.totalWithoutTax, record.currencyIsoCode),
    },
    {
      title: 'Cost',
      key: EWarehouseOrdersFilterColumn.COST,
      align: 'right',
      ...getColumnNumberRange<TWarehouseOrderRow>({
        missingLabel: 'Missing cost',
      }),
      filteredValue: toNumberRangeFilterKeys(filters.purchasePriceEur),
      render: (_value: string, record: TWarehouseOrderRow): string =>
        formatEur(record.purchasePriceEur),
    },
    {
      title: 'Profit',
      key: EWarehouseOrdersFilterColumn.PROFIT,
      align: 'right',
      ...getColumnNumberRange<TWarehouseOrderRow>({
        missingLabel: 'Missing profit',
      }),
      filteredValue: toNumberRangeFilterKeys(filters.profitEur),
      render: (
        _value: string,
        record: TWarehouseOrderRow,
      ): React.ReactElement => <ProfitText value={record.profitEur} />,
    },
  ];
};

function WarehouseOrdersContainer(): React.ReactElement {
  const dispatch = useAppDispatch();
  const list = useSelector((s: RootState) => s.WarehouseOrder.list);
  const inProcess = useSelector((s: RootState) => s.WarehouseOrder.inProcess);
  const isListStale = useSelector(
    (s: RootState) => s.WarehouseOrder.isListStale,
  );
  const warehouses = useSelector((s: RootState) => s.Warehouse.warehouses);
  const [searchParams, setSearchParams] = useSearchParams();

  const filters: IWarehouseOrderListFilters = parseFilters(searchParams);
  const page: number =
    Number(searchParams.get(EWarehouseOrdersSearchParam.PAGE)) || 1;
  const pageSize: number =
    Number(searchParams.get(EWarehouseOrdersSearchParam.PAGE_SIZE)) ||
    DEFAULT_PAGE_SIZE;
  const queryKey: string = JSON.stringify({ filters, page, pageSize });

  const load = (): void => {
    dispatch(warehouseOrdersLoad({ filters, page, pageSize }));
  };

  useEffect(load, [dispatch, queryKey]);

  const onTableChange = (
    _pagination: object,
    tableFilters: Record<string, FilterValue | null>,
    _sorter: SorterResult<object> | SorterResult<object>[],
    extra: TableCurrentDataSource<object>,
  ): void => {
    if (extra.action !== 'filter') {
      return;
    }

    const [from, to] = (
      tableFilters[EWarehouseOrdersFilterColumn.ORDERED] ?? []
    ).map(String);
    const hasRange: boolean = Boolean(from && to);

    const nextParams: Record<string, string> = {
      [EWarehouseOrdersSearchParam.WAREHOUSE]: joinFilterValue(
        tableFilters[EWarehouseOrdersFilterColumn.WAREHOUSE],
      ),
      [EWarehouseOrdersSearchParam.STATUS]: joinFilterValue(
        tableFilters[EWarehouseOrdersFilterColumn.STATUS],
      ),
      [EWarehouseOrdersSearchParam.PAIRING]: joinFilterValue(
        tableFilters[EWarehouseOrdersFilterColumn.PAIRING],
      ),
      [EWarehouseOrdersSearchParam.FROM]: hasRange ? from : '',
      [EWarehouseOrdersSearchParam.TO]: hasRange ? to : '',
      [EWarehouseOrdersSearchParam.SEARCH]: String(
        tableFilters[EWarehouseOrdersFilterColumn.CODE]?.[0] ?? '',
      ).trim(),
      [EWarehouseOrdersSearchParam.ORDER_SEARCH]: String(
        tableFilters[EWarehouseOrdersFilterColumn.ORDERS]?.[0] ?? '',
      ).trim(),
      ...toRangeSearchParams(
        tableFilters[EWarehouseOrdersFilterColumn.ITEMS],
        ITEMS_SEARCH_PARAMS,
      ),
      ...toRangeSearchParams(
        tableFilters[EWarehouseOrdersFilterColumn.COST],
        COST_SEARCH_PARAMS,
      ),
      ...toRangeSearchParams(
        tableFilters[EWarehouseOrdersFilterColumn.PROFIT],
        PROFIT_SEARCH_PARAMS,
      ),
    };

    Object.entries(nextParams).forEach(
      ([key, value]: [string, string]): void => {
        if (value) {
          searchParams.set(key, value);
        } else {
          searchParams.delete(key);
        }
      },
    );

    searchParams.delete(EWarehouseOrdersSearchParam.PAGE);
    setSearchParams(searchParams);
  };

  const dataSource = useMemo<TWarehouseOrderRow[]>(
    () =>
      (list?.items ?? []).map((item: IWarehouseOrderListItem) => ({
        key: item.uuid,
        ...item,
      })),
    [list],
  );

  return (
    <Space size={SPACING.lg} orientation="vertical" style={{ width: '100%' }}>
      <DetailPageHeader
        title="Warehouse orders"
        onReload={load}
        isReloadDisabled={inProcess}
        isReloadHighlighted={isListStale}
        actions={
          <>
            <SyncButton />
            <Popconfirm
              title="Recalculate all warehouse orders?"
              description="Purchase cost, supplier fees and profit are recalculated for every warehouse order."
              onConfirm={(): void => {
                dispatch(
                  warehouseOrdersRecalculate({ filters, page, pageSize }),
                );
              }}
            >
              <Button icon={<CalculatorOutlined />} disabled={inProcess}>
                Recalculate all
              </Button>
            </Popconfirm>
          </>
        }
        info={
          isListStale && (
            <Text type="secondary">New data from the warehouses</Text>
          )
        }
      />

      <TableComponent
        columns={
          getColumns({
            filters,
            searchParams,
            warehouses: warehouses ?? [],
          }) as Parameters<typeof TableComponent>[0]['columns']
        }
        dataSource={dataSource}
        isLoading={inProcess}
        emptyText="No warehouse orders match the filter"
        size="small"
        scroll={{ x: 'max-content' }}
        onChange={onTableChange}
        pagination={{
          current: page,
          pageSize,
          total: list?.total ?? 0,
          onChange: (nextPage: number, nextPageSize: number): void => {
            searchParams.set(
              EWarehouseOrdersSearchParam.PAGE,
              String(nextPage),
            );
            searchParams.set(
              EWarehouseOrdersSearchParam.PAGE_SIZE,
              String(nextPageSize),
            );
            setSearchParams(searchParams);
          },
        }}
      />
    </Space>
  );
}

export default WarehouseOrdersContainer;
